/**
 * Tenant Onboarding Wizard
 *
 * Self-contained wizard for tenant-specific onboarding.
 * Handles its own API calls, steps, and completion logic.
 */

import React, { useCallback, useState } from 'react';
import { DevicePairingDialog } from '@/components/device-pairing';
import { startOnboardingFlow } from '@/features/onboarding/api/onboarding-api';
import { FailedStep } from '@/features/onboarding/components/steps/failed-step';
import { TenantCompleteStep } from '@/features/onboarding/components/steps/tenant-complete-step';
import { TenantWelcomeStep } from '@/features/onboarding/components/steps/tenant-welcome-step';
import { FactorSteps } from '@/features/onboarding/components/wizards/shared/factor-steps';
import { defaultShouldAutoComplete } from '@/features/onboarding/hooks/use-factor-actions';
import { useOnboardingFlow } from '@/features/onboarding/hooks/use-onboarding-flow';
import {
  FactorType,
  FlowStatus,
  type OnboardingFlowResponse,
} from '@/features/onboarding/types/onboarding.types';

interface TenantOnboardingWizardProps {
  token: string;
  userName?: string;
  companyName?: string;
  tenantUrl?: string;
  onComplete: () => void;
}

/**
 * Tenant Onboarding Wizard
 *
 * Flow:
 * 1. Tenant welcome step → calls startTenantOnboarding API
 * 2. Factor steps (SMS, Password, TOTP, etc.)
 * 3. Device pairing dialog (skippable, only if NewCore App was NOT enrolled)
 * 4. Tenant complete step → activates tenant, then redirects to admin-console
 */
export const TenantOnboardingWizard: React.FC<TenantOnboardingWizardProps> = ({
  token,
  userName,
  companyName,
  tenantUrl,
  onComplete,
}) => {
  // Local UX detour: show the device-pairing dialog when ready to complete
  // and push notification wasn't enrolled. See UserOnboardingWizard for
  // the rationale — /complete clears the onboarding session cookie, so
  // the dialog must run before /complete is called.
  const [hasHandledDevicePairing, setHasHandledDevicePairing] = useState(false);

  const willEnrollPush = useCallback(
    (response: OnboardingFlowResponse): boolean =>
      (response.factors ?? []).some(
        factor =>
          factor.factorType === FactorType.PUSH_NOTIFICATION &&
          factor.isEnrolled
      ),
    []
  );

  const shouldAutoComplete = useCallback(
    (response: OnboardingFlowResponse): boolean => {
      if (!defaultShouldAutoComplete(response)) {
        return false;
      }

      return willEnrollPush(response);
    },
    [willEnrollPush]
  );

  const flow = useOnboardingFlow({
    token,
    startApi: startOnboardingFlow,
    shouldAutoComplete,
  });

  const dismissDevicePairing = useCallback(() => {
    setHasHandledDevicePairing(true);
    void flow.handleFinalComplete();
  }, [flow]);

  const handleFinalComplete = useCallback(async () => {
    await flow.handleFinalComplete();
    onComplete();
  }, [flow, onComplete]);

  // Tenant welcome step
  if (!flow.state.hasStarted) {
    return (
      <TenantWelcomeStep
        userName={userName}
        companyName={companyName ?? ''}
        tenantUrl={tenantUrl}
        onGetStarted={flow.handleGetStarted}
        isLoading={flow.state.isLoading}
      />
    );
  }

  // Failed step
  if (flow.state.flowStatus === FlowStatus.FAILED) {
    return (
      <FailedStep error={flow.state.error} onClearError={flow.clearError} />
    );
  }

  // Device pairing step — fires when the backend signals "ready to complete"
  // and push wasn't enrolled. Must render BEFORE /complete clears the
  // onboarding session cookie.
  const pushEnrolled = flow.state.enrolledFactors.includes(
    FactorType.PUSH_NOTIFICATION
  );

  if (
    flow.state.canComplete &&
    flow.state.currentFactorType === null &&
    !flow.state.hasAlternative &&
    !pushEnrolled &&
    !hasHandledDevicePairing
  ) {
    return (
      <DevicePairingDialog
        isOpen={true}
        onClose={dismissDevicePairing}
        cancelButtonText='Skip'
        isOnboarding={true}
        showIntro={true}
      />
    );
  }

  // Tenant complete step
  if (flow.state.flowStatus === FlowStatus.COMPLETED) {
    return (
      <TenantCompleteStep
        enrolledFactors={flow.state.enrolledFactors}
        userName={userName}
        isExchanging={flow.state.isExchanging}
        isAuthenticated={flow.state.isAuthenticated}
        exchangeError={flow.state.exchangeError}
        onExchange={flow.exchangeForSession}
        onComplete={handleFinalComplete}
      />
    );
  }

  // Factor steps (shared)
  return (
    <FactorSteps
      state={flow.state}
      onSubmitFactor={flow.submitFactor}
      onClearError={flow.clearError}
      onResend={flow.resendSmsCode}
      canResendSms={flow.canResendSms}
      onChooseAlternative={flow.chooseAlternative}
      onBackToAlternatives={flow.goBackToAlternatives}
      onContinue={flow.completeSecondaryFactors}
    />
  );
};
